// Пиксели сетки: от произвольной даты до произвольной, ширина дня задаёт экран (роадмап 12px, лонч 32px),
// колонки недель и положение баров. Чистые функции.
import { addDays, daysBetween, dayLabel, mondayOf, monthStartOf, monthYearLabel, nextMonthStart, nextQuarterStart, quarterLabel, quarterStartOf, type ISODate } from '@core/dates';

export const WEEK_WIDTH = 84; // кратно 7: день ровно 12px, линии дней без сглаживания
export const DAY_WIDTH = WEEK_WIDTH / 7;
export const LAUNCH_DAY_WIDTH = 32; // доска лонча по дням: названия читаются, две недели — 450px
export const BAR_HEIGHT = 36;
export const BAR_HEIGHT_TWO = 48;  // полные названия: две строки 14px
export const BAR_HEIGHT_FULL = 60; // полные названия: три строки 12px
export const BAR_TEXT_PAD = 16;    // px-2 с двух сторон
const TRACK_GAP = 4;
const ROW_PADDING = 4;
export const HEADER_ROW = 28; // строка проекта без задач, но с подпроектами: только подпись
export const LABEL_WIDTH = 240; // колонка названий одна на все экраны: при переключении вкладок сетка не прыгает
export const HEADER_ROWS = { quarters: 24, months: 28, weeks: 20 }; // три строки шапки сетки
export const HEADER_HEIGHT = HEADER_ROWS.quarters + HEADER_ROWS.months + HEADER_ROWS.weeks;
export const MIN_MONTH_LABEL = 64; // уже — подпись месяца не помещается, огрызок у края без подписи
export const LANE_HEIGHT = 20; // дорожка полосы контекста (события, отпуска, закреплённые)
export const VACATION_STRIP = 16; // подпись отпуска над барами (People)
export const TOOLBAR_HEIGHT = 49; // контролы 32 + отступы по 8 + нижняя граница

export type DragKind = 'move' | 'resize-start' | 'resize-end';

export type WeekColumn = {
  monday: ISODate;
  day: string;   // «7»
  left: number;  // понедельник в пикселях от начала сетки; у первой, неполной недели — левее нуля
};

/** Сетка: первый и последний день включительно, ширина по дням, колонки задетых недель. */
export type Grid = { start: ISODate; end: ISODate; days: number; width: number; columns: WeekColumn[]; dayWidth: number };

/** Сетка ровно от from до to: крайние недели режутся, а не дорисовываются до понедельника. dayWidth — ширина дня (лонч: 32px). */
export function gridOf(from: ISODate, to: ISODate, dayWidth = DAY_WIDTH): Grid {
  const end = to < from ? from : to;
  const days = daysBetween(from, end) + 1;
  const columns: WeekColumn[] = [];
  for (let m = mondayOf(from); m <= end; m = addDays(m, 7)) {
    columns.push({ monday: m, day: dayLabel(m), left: daysBetween(from, m) * dayWidth });
  }
  return { start: from, end, days, width: days * dayWidth, columns, dayWidth };
}

export type BarBox = { left: number; width: number; clippedLeft: boolean; clippedRight: boolean };

/** Положение бара по датам относительно начала сетки — ровно по дням, без дорисовки. */
export function barGeometry(task: { start_date: ISODate; end_date: ISODate }, grid: Grid): BarBox | null {
  if (task.start_date > grid.end || task.end_date < grid.start) return null;
  const startDay = daysBetween(grid.start, task.start_date);
  const endDay = daysBetween(grid.start, task.end_date);
  const clippedLeft = startDay < 0;
  const clippedRight = endDay > grid.days - 1;
  const s = Math.max(0, startDay);
  const e = Math.min(grid.days - 1, endDay);
  return { left: s * grid.dayWidth, width: (e - s + 1) * grid.dayWidth, clippedLeft, clippedRight };
}

/**
 * Где рисовать бар во время перетаскивания: взятый едет за мышью (move) или уже привязан
 * к целевым датам (resize); член выделенной группы сдвигается на те же дни.
 */
export function barStyle(box: BarBox, d: { dragged: boolean; kind?: DragKind; dx?: number; previewBox?: BarBox | null; shiftDays?: number | null }, dayWidth = DAY_WIDTH): { left: number; width: number } {
  if (d.shiftDays != null) return { left: box.left + d.shiftDays * dayWidth, width: box.width };
  if (!d.dragged) return { left: box.left, width: box.width };
  if (d.kind === 'move') return { left: box.left + (d.dx ?? 0), width: box.width };
  return d.previewBox ? { left: d.previewBox.left, width: d.previewBox.width } : { left: box.left, width: box.width };
}

/** Дата под координатой x (от начала сетки); за краями — крайний день. */
export function dateAtX(x: number, grid: Grid): ISODate {
  const day = Math.floor(x / grid.dayWidth);
  return addDays(grid.start, Math.max(0, Math.min(grid.days - 1, day)));
}

/** Сдвиг мыши в днях с шагом step (7 — по неделям, 1 — по дням). */
export const snapDays = (dx: number, step: number, dayWidth = DAY_WIDTH): number => Math.round(dx / dayWidth / step) * step;

/**
 * Треки, занятые видимыми задачами, по возрастанию — строка рисуется без пустых треков.
 * Слот на экране = индекс в этом списке; последний, пустой слот — для «бросить сюда».
 */
export function visibleSlots(tasks: { track: number | null; in_backlog: boolean }[]): number[] {
  const set = new Set<number>();
  for (const t of tasks) if (!t.in_backlog) set.add(t.track ?? 0);
  return [...set].sort((a, b) => a - b);
}

/** Строк названия при переносе по словам в ширину width; слово шире строки занимает свою. */
export function titleLines(title: string, width: number, measure: (s: string) => number): number {
  const words = title.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 1;
  const space = measure(' ');
  let lines = 1;
  let cur = 0;
  for (const w of words) {
    const ww = measure(w);
    if (cur === 0) cur = ww;
    else if (cur + space + ww <= width) cur += space + ww;
    else { lines++; cur = ww; }
  }
  return lines;
}

/** Высота бара строки в режиме полных названий: по самому длинному видимому названию — 36, 48 или 60. */
export function barHeightFor(tasks: { title: string; start_date: ISODate; end_date: ISODate }[], grid: Grid, measure: (s: string) => number): number {
  let h = BAR_HEIGHT;
  for (const t of tasks) {
    const box = barGeometry(t, grid);
    if (!box) continue;
    const lines = titleLines(t.title, box.width - BAR_TEXT_PAD, measure);
    h = Math.max(h, lines <= 1 ? BAR_HEIGHT : lines === 2 ? BAR_HEIGHT_TWO : BAR_HEIGHT_FULL);
    if (h === BAR_HEIGHT_FULL) break;
  }
  return h;
}

const GLYPH_WIDTH = 16;      // квадратик ★ ! ✓ (w-4)
const GLYPH_GAP = 2;         // между глифами (gap-0.5)
const GLYPHS_MARGIN = 4;     // после глифов (mr-1)
const MIN_TITLE_WIDTH = 24;  // уже — название на баре не рисуется, остаются одни глифы

/**
 * Влезает ли название в бар: не влезает — обрезается маской (без «…»), а если после глифов
 * места меньше MIN_TITLE_WIDTH — не рисуется вовсе. glyphs — сколько глифов ★ ! ✓ перед названием.
 */
export function barTitleFit(title: string, boxWidth: number, glyphs: number, measure: (s: string) => number): { clipped: boolean; titleHidden: boolean } {
  const avail = boxWidth - BAR_TEXT_PAD;
  const marks = glyphs ? glyphs * GLYPH_WIDTH + (glyphs - 1) * GLYPH_GAP + GLYPHS_MARGIN : 0;
  const clipped = marks + measure(title) > avail;
  return { clipped, titleHidden: clipped && glyphs > 0 && avail - marks < MIN_TITLE_WIDTH };
}

export const rowHeight = (tracks: number, bar = BAR_HEIGHT): number => ROW_PADDING * 2 + tracks * bar + (tracks - 1) * TRACK_GAP;
export const trackTop = (track: number, bar = BAR_HEIGHT): number => ROW_PADDING + track * (bar + TRACK_GAP);
export const trackAtY = (y: number, bar = BAR_HEIGHT): number => Math.max(0, Math.floor((y - ROW_PADDING) / (bar + TRACK_GAP)));

type PeriodSpan = { key: ISODate; label: string; left: number; width: number };

/**
 * Месяцы или кварталы по реальным датам, а не по неделям: отрезки в пикселях внутри сетки.
 * Крайние периоды обрезаны по краю диапазона.
 */
export function periodSpans(grid: Grid, unit: 'month' | 'quarter'): PeriodSpan[] {
  const startOf = unit === 'month' ? monthStartOf : quarterStartOf;
  const nextOf = unit === 'month' ? nextMonthStart : nextQuarterStart;
  const label = unit === 'month' ? monthYearLabel : quarterLabel;
  const spans: PeriodSpan[] = [];
  for (let p = startOf(grid.start); daysBetween(grid.start, p) < grid.days; p = nextOf(p)) {
    const from = Math.max(0, daysBetween(grid.start, p));
    const to = Math.min(grid.days, daysBetween(grid.start, nextOf(p)));
    spans.push({ key: p, label: label(p), left: from * grid.dayWidth, width: (to - from) * grid.dayWidth });
  }
  return spans;
}

/** Подряды в строке: пересекающиеся отрезки — на разных, остальные делят нулевой. */
export function assignSubRows(items: { id: string; start_date: ISODate; end_date: ISODate }[]): Map<string, number> {
  const sorted = [...items].sort((a, b) => (a.start_date < b.start_date ? -1 : a.start_date > b.start_date ? 1 : a.id < b.id ? -1 : 1));
  const rowEnds: ISODate[] = [];
  const out = new Map<string, number>();
  for (const t of sorted) {
    let placed = false;
    for (let r = 0; r < rowEnds.length; r++) {
      if (t.start_date > rowEnds[r]!) { rowEnds[r] = t.end_date; out.set(t.id, r); placed = true; break; }
    }
    if (!placed) { out.set(t.id, rowEnds.length); rowEnds.push(t.end_date); }
  }
  return out;
}
